import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './Notifications.scss';

import NotificationsCatalog from './NotificationsCatalog';
import NotificationsPreview from './NotificationsPreview';
import NotificationsRecipients from './NotificationsRecipients';
import NotificationsSends from './NotificationsSends';
import NotificationsRegistry from './NotificationsRegistry';
import NotificationsRegistryEntry from './NotificationsRegistryEntry';
import NotificationsRegistryNew from './NotificationsRegistryNew';
import NotificationsLaunches from './NotificationsLaunches';

import { getEmailTemplates, getNotificationLaunches, getNotificationRecipientTemplates } from '../../api/notifications';
import { getUrlParam } from '../../helpers/helpers';

const VIEWS = [
  {
    key: 'registry',
    label: 'Registry',
    title: 'Notification Registry',
    description: (
      <>
        A space for Product and Marketing to plan new notifications and see what notifications already exist. See how to use the Registry guide{' '}
        <a href='https://app.notion.com/p/shopmyshelf/How-to-use-the-Registry-3da3a75a96c581e9ad83f5761c18f7a3' target='_blank' rel='noreferrer'>
          here
        </a>
      </>
    )
  },
  {
    key: 'launches',
    label: 'Launches',
    title: 'Notification Launches',
    description: 'Every notification that is built and not fully live. To change Mode requires the launch_notifications scope.'
  },
  {
    key: 'templates',
    label: 'Templates',
    title: 'Notification Templates',
    description: 'The notifications that exist in code today. Preview one with example data and send yourself a test.'
  },
  {
    key: 'recipients',
    label: 'Recipients',
    title: 'Notification Recipients',
    description: 'Look up one person: what they were sent, what was held back and why, and which switches they have turned off.'
  },
  {
    key: 'sends',
    label: 'Sends',
    title: 'Notification Sends',
    description: 'What each notification did over a period: how many were sent, how many were held and why.'
  }
];

const Notifications = ({ canManageMarketing = false, canChangeRollout = false }) => {
  const [templates, setTemplates] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [linkedTemplateName, setLinkedTemplateName] = useState(() => (canManageMarketing ? getUrlParam('template') : null));
  const [view, setView] = useState(canManageMarketing ? 'registry' : 'recipients');
  // ?registry=<short code> opens a notification straight from a link, such as the drift sweep's.
  const [registryEntryType, setRegistryEntryType] = useState(() => (canManageMarketing ? getUrlParam('registry') : null));
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [toolbarSlot, setToolbarSlot] = useState(null);
  const [headerEl, setHeaderEl] = useState(null);
  const [sendsCache, setSendsCache] = useState({});
  const cacheSends = (range, result) => setSendsCache(existing => ({ ...existing, [range]: result }));
  const [recipientsView, setRecipientsView] = useState(null);
  // The last recipient looked at is also the one a template preview opens with.
  const rememberRecipientsView = useCallback(view => {
    setRecipientsView(view);
    setRecipient(view.recipient);
  }, []);
  const [registryView, setRegistryView] = useState(null);
  const rememberRegistryView = useCallback(view => setRegistryView(view), []);
  const [launches, setLaunches] = useState(null);
  const [launchesFailed, setLaunchesFailed] = useState(false);
  const reloadLaunches = useCallback(
    () =>
      getNotificationLaunches()
        .then(resp => {
          setLaunches(resp);
          setLaunchesFailed(false);
        })
        .catch(() => {
          setLaunchesFailed(true);
          window.ALERT?.error?.('Failed to load Launches.');
        }),
    []
  );
  useEffect(() => {
    if (canManageMarketing) reloadLaunches();
  }, [canManageMarketing, reloadLaunches]);
  const navHeight = useAdminNavHeight();
  const headerHeight = useElementHeight(headerEl);

  useEffect(() => {
    const fetchTemplates = canManageMarketing ? getEmailTemplates : getNotificationRecipientTemplates;
    fetchTemplates()
      .then(resp => setTemplates(resp.templates || []))
      .catch(() => window.ALERT?.error?.('Failed to load email templates.'))
      .finally(() => setIsFetching(false));
  }, [canManageMarketing]);

  const templatesByName = useMemo(() => _.keyBy(templates, 'name'), [templates]);
  const selectedTemplate = activeTemplate || (linkedTemplateName ? templatesByName[linkedTemplateName] : null);

  // Opening a template from a recipient's log keeps that recipient loaded in the detail's user lookup.
  const openTemplate = (template, user = null) => {
    setRecipient(user);
    setActiveTemplate(template);
  };
  const closePreview = () => {
    window.history.replaceState(null, '', '/notifications');
    setActiveTemplate(null);
    setLinkedTemplateName(null);
  };
  const openRegistryEntryFromPreview = type => {
    closePreview();
    setRegistryEntryType(type);
  };

  if (linkedTemplateName && isFetching) {
    return <div className='notifications-outer-container' role='status'>Loading template…</div>;
  }
  if (linkedTemplateName && !selectedTemplate) {
    return (
      <div className='notifications-outer-container'>
        <p className='notifications-missing-template' role='alert'>Template “{linkedTemplateName}” was not found.</p>
        <button className='notifications-missing-template-back' type='button' onClick={closePreview}>Back to Notifications</button>
      </div>
    );
  }

  if (selectedTemplate) {
    return (
      <div className='notifications-outer-container'>
        <NotificationsPreview
          template={selectedTemplate}
          initialUser={recipient}
          onBack={closePreview}
          onOpenRegistryEntry={openRegistryEntryFromPreview}
        />
      </div>
    );
  }

  // A lane's Mode or holdout may have moved on its page, so Launches and its tab count read again.
  const leaveRegistryPage = () => {
    setRegistryEntryType(null);
    setIsCreatingEntry(false);
    reloadLaunches();
  };

  if (registryEntryType) {
    return (
      <div className='notifications-outer-container'>
        <NotificationsRegistryEntry
          type={registryEntryType}
          onBack={leaveRegistryPage}
          onOpenEntry={setRegistryEntryType}
          canChangeRollout={canChangeRollout}
          onOpenTemplate={name => templatesByName[name] && openTemplate(templatesByName[name])}
        />
      </div>
    );
  }

  if (isCreatingEntry) {
    return (
      <div className='notifications-outer-container'>
        <NotificationsRegistryNew
          onCancel={leaveRegistryPage}
          onCreated={type => setRegistryEntryType(type)}
          canChangeRollout={canChangeRollout}
          stickyTop={navHeight}
        />
      </div>
    );
  }

  const activeView = VIEWS.find(v => v.key === view);

  // The nav, the page header (title, tabs and the active tab's controls) and each table's column
  // headers stack as three sticky layers; the tables read the header's bottom edge from the variable.
  return (
    <div className='notifications-outer-container' style={{ '--sticky-below-header': `${navHeight + headerHeight}px` }}>
      <div className='notifications-header' style={{ top: navHeight }} ref={setHeaderEl}>
        <div className='title-row'>
          <h1 className='title'>{activeView?.title || 'Notifications'}</h1>
          <div className='view-tabs'>
            {VIEWS.filter(v => canManageMarketing || v.key === 'recipients').map(v => (
              <div key={v.key} className={cn('view-tab', { active: view === v.key })} onClick={() => setView(v.key)}>
                {v.label}
                {v.key === 'launches' && launches?.rows && (
                  <span className='view-tab-count'>{launches.rows.filter(row => row.group !== 'paused').length}</span>
                )}
              </div>
            ))}
          </div>
          {view === 'registry' && (
            <button type='button' className='registry-new' onClick={() => setIsCreatingEntry(true)}>
              + New Planned Notification
            </button>
          )}
        </div>
        <div className='subtitle'>
          {activeView?.description}
          {view === 'templates' && <span className='count'>{isFetching ? 'Loading...' : `${templates.length} templates`}</span>}
        </div>
        <div className='toolbar-slot' ref={setToolbarSlot} />
      </div>
      {view === 'registry' && (
        <NotificationsRegistry
          onOpenEntry={setRegistryEntryType}
          toolbarSlot={toolbarSlot}
          initialView={registryView}
          onViewChange={rememberRegistryView}
        />
      )}
      {view === 'launches' && (
        <NotificationsLaunches
          rows={launches?.rows || null}
          planned={launches?.planned || 0}
          loading={!launches && !launchesFailed}
          onReload={reloadLaunches}
          canChangeRollout={canChangeRollout}
          onOpenEntry={setRegistryEntryType}
          onOpenPlanned={() => {
            rememberRegistryView({ stage: 'planned' });
            setView('registry');
          }}
          toolbarSlot={toolbarSlot}
        />
      )}
      {view === 'templates' && (
        <NotificationsCatalog
          templates={templates}
          isFetching={isFetching}
          onSelectTemplate={canManageMarketing ? openTemplate : undefined}
          toolbarSlot={toolbarSlot}
        />
      )}
      {view === 'recipients' && (
        <NotificationsRecipients
          initialUser={recipient}
          initialView={recipientsView}
          onViewChange={rememberRecipientsView}
          templatesByName={templatesByName}
          onSelectTemplate={canManageMarketing ? openTemplate : undefined}
          toolbarSlot={toolbarSlot}
        />
      )}
      {view === 'sends' && (
        <NotificationsSends
          templatesByName={templatesByName}
          onSelectTemplate={template => template && openTemplate(template)}
          toolbarSlot={toolbarSlot}
          cache={sendsCache}
          onCache={cacheSends}
        />
      )}
    </div>
  );
};

Notifications.propTypes = { canManageMarketing: PropTypes.bool, canChangeRollout: PropTypes.bool };

// The header's height changes with the active tab's controls; the tables' sticky column headers follow it.
// Keyed on the element itself (a callback ref), so a header that remounts after a template preview is
// measured afresh rather than the observer staying on the detached one.
const useElementHeight = el => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!el) return undefined;
    const measure = () => setHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return height;
};

// The admin nav is sticky at the top; the page header sticks just beneath it.
const useAdminNavHeight = () => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const nav = document.querySelector('.admin-nav-header');
    if (!nav) return;
    const measure = () => setHeight(nav.offsetHeight);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return height;
};

export default Notifications;
